import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { context } from 'bookingbug-core-js';
import { environment } from '../../environments/environment';

/**
 * Civica PAYLINK pre-basket (book-after-payment) integration for
 * Havering — talks to bl-civica-api (`/payment/civica/pre-basket*`)
 * with `payment_provider: 'paylink'`.
 *
 * Flow (mirrors the live Solihull Civica eStore pre-basket standard):
 *   1. preBasketInvoke — persists the booking intent to DynamoDB
 *      (bl_purchase_logs_{client}) and builds the Paylink payment URL from
 *      the council's SSM patterns. Returns the gateway redirect `url`.
 *   2. The browser pays at Civica Paylink. `return_url` becomes Paylink's
 *      ReceiptPageFinishURL (the payer lands back on the verify page when
 *      they click Finish on Civica's receipt page); `back_url` is Paylink's
 *      BackButtonURL (payer backed out before paying). The payment
 *      notification itself goes server-to-server to bl-civica-api's own
 *      receiver, never to this app — in one delivery mode that receiver
 *      302s the browser on to `return_url` with `basket`, `service_item`
 *      and `response_code` appended as query params.
 *   3. The n8n Civica Post-Basket Verify workflow checks out the JRNI
 *      basket, links the extra items carried in `data`, and marks the row
 *      PAID.
 *   4. preBasketFind — polled by verify-payment until the row shows the
 *      JRNI purchase (data.purchase_id / status PAID).
 *
 * Contract notes:
 *   - `amounts` are POUNDS, one line carries the whole payable total; the
 *     itemised fees ride in `data.fees` so the worker can link each extra
 *     item and recompute the price server-side.
 *   - `company_id` MUST be the PARENT (token-holding) company;
 *     `child_company` is the location the booking sits under.
 *   - `basket` MUST be a blApi `basket-public` basket that already holds
 *     the service item — the worker checks out by this id.
 */
@Injectable({
  providedIn: 'root',
})
export class CivicaPaylinkService {
  // bl-civica-api sits behind the same host as the rest of the bl API, so this
  // follows api.service.ts rather than the branch-pinned hardcoding some of the
  // older payment services use — that way develop and main can carry the same
  // file.
  private url = environment.production
    ? 'https://api.bookinglab.co.uk'
    : 'https://staging.bookinglab.co.uk';
  // private url = 'http://localhost:4201';
  private path = '/';
  private civicaUrlPath = '/payment/';

  constructor(private http: HttpClient) {}

  async preBasketInvoke(
    company,
    service,
    basket,
    serviceItems,
    descriptions,
    client,
    fundCodes,
    amounts,
    authToken,
    data,
    slot,
    extraDuration,
    journeyRoute,
    reference,
    parentCompanyId?,
    costCentre?
  ) {
    let port = '';
    // '' is the default port for the scheme -- without it a plain https
    // page scored a stray ":" into the payment gateway URL.
    const ports: any = ['80', '443', ''];
    if (ports.indexOf(window.location.port) === -1) {
      port = `:${window.location.port}`;
    }

    let serviceItemString = '';
    for (const serviceItem of serviceItems) {
      serviceItemString = `${serviceItemString},${serviceItem.id}`;
    }

    let returnUrl = '';
    if (journeyRoute == 'order_a_copy_certificate') {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${port}/start/${journeyRoute}/verify/${company.id}/${basket.id}/${serviceItemString}`;
    } else {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify/${company.id}/${basket.id}/${serviceItemString}`;
    }

    console.log(`journeyRoute: ${journeyRoute} Return URL: ${returnUrl}`);

    const body: any = {
      host: `${context.apiUrl}`,
      payment_provider: 'paylink',
      // company_id MUST be the PARENT (token-holding) company; child_company is
      // the location the booking sits under. The post-payment worker resolves
      // the token holder from parent_company_id first.
      company_id:
        parentCompanyId === undefined ? `${company.id}` : parentCompanyId,
      child_company: `${company.id}`,
      service_id: `${service.id}`,
      client_id: `${client.id}`,
      basket: `${basket.id}`,
      service_items: serviceItems,
      descriptions: descriptions,
      data: data,
      return_url: `${returnUrl}`,
      // The ?cancelled=true marker lets the verify page tell a pre-payment
      // back-out (Paylink's BackButtonURL lands here) apart from a normal
      // return, so it can clear the basket and restart the journey.
      back_url: `${returnUrl}?cancelled=true`,
      auth_token: `${authToken}`,
      slot: slot,
      extra_duration: extraDuration,
      journey_route: journeyRoute,
      reference: `${reference}`,
    };

    if (costCentre) {
      // Feeds the #costCentre# token in the council's SSM Reference /
      // PaymentPattern; the server falls back to the child company's
      // location_ledger_code when absent.
      body.cost_centre = `${costCentre}`;
    }

    if (Array.isArray(amounts)) {
      body.amounts = amounts;
    } else {
      body.amount = `${amounts}`;
    }
    if (Array.isArray(fundCodes)) {
      body.fund_codes = fundCodes;
    } else {
      body.fund_code = `${fundCodes}`;
    }

    const result: any = await this.http
      .post(`${this.url}${this.civicaUrlPath}civica/pre-basket`, body)
      .toPromise();
    return { ...result, url: result?.url };
  }

  // Poll a pre-basket row back by basket + service item. The row's status
  // (INITIATED → NEW → workflow-owned terminals) and data.purchase_id tell
  // the verify page when the backend worker has created the JRNI booking.
  async preBasketFind(companyId, basketId, serviceItemId) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
    };
    return this.http
      .post(`${this.url}${this.civicaUrlPath}civica/pre-basket/find`, body)
      .toPromise();
  }

  async preBasketUpdate(companyId, basketId, serviceItemId, status, ref, data) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
      ref: ref,
      status: `${status}`,
      data: data,
    };
    return this.http
      .post(`${this.url}${this.civicaUrlPath}civica/pre-basket/update`, body)
      .toPromise();
  }
}
